/**
 * Customer data export (Settings -> "Data and export").
 *
 * Two kinds of download, because a customer's work lives in two places:
 *
 * 1. The control plane (`POST /v1/projects/{id}/export`, `POST /v1/account/export`): the
 *    front door builds a zip and streams it back; this page saves it. These cover tool
 *    outputs, hosted-task outputs, owner-bridge uploads and account records only.
 * 2. The workspace (Story, Prep, Launch and Learning files): a plain link to the workspace's
 *    own download route. The browser must follow it as a user click from THIS origin, with
 *    the workspace session cookie it already holds after the customer used Open workspace; the workspace
 *    refuses anything else (no Referer from this origin, no user activation, a script
 *    fetch). So it is a real `<a href>`, never a `fetch`, and it must not carry
 *    `rel="noreferrer"` (that drops the Referer the workspace requires).
 */

import { ApiClientError, apiUrl } from './http';

/** The workspace's download route (`workspace_export.ROUTE_PATH`). */
export const WORKSPACE_EXPORT_PATH = '/workspace-export/download';

const WORKSPACE_ID = /^wsp_[0-9a-f]{32}$/;
const SLUG_UNSAFE = /[^A-Za-z0-9._-]+/g;
const DOWNLOAD_TIMEOUT_MS = 120_000;

export interface ExportFile {
  blob: Blob;
  filename: string;
}

function malformed(message: string): ApiClientError {
  return new ApiClientError('malformed', 200, 'malformed_response', message);
}

/** A filename built only from letters, digits, dot, dash and underscore. */
export function safeFilename(stem: string, fallback: string): string {
  const cleaned = String(stem || '').replace(SLUG_UNSAFE, '-').replace(/^[-.]+|[-.]+$/g, '').slice(0, 60);
  return `${cleaned || fallback}.zip`;
}

/**
 * The workspace download link: exactly `https://<ingress host>/workspace-export/download`, where the host
 * is the one `GET /v1/workspaces` listed for this workspace and starts with `wsp-<its 32 hex>.`. Anything
 * else (another workspace, a look-alike host, credentials, a port) is refused, so this page never links a
 * customer to a place that is not their own workspace.
 */
export function workspaceExportUrl(workspaceId: string, ingressHost: string): string {
  if (!WORKSPACE_ID.test(workspaceId)) throw malformed('the workspace id is malformed');
  const label = `wsp-${workspaceId.slice('wsp_'.length)}.`;
  const host = String(ingressHost || '').toLowerCase();
  if (!host.startsWith(label) || host.length <= label.length) {
    throw malformed('the listed ingress host is not this workspace\'s');
  }
  const raw = `https://${host}${WORKSPACE_EXPORT_PATH}`;
  const url = new URL(raw);
  if (
    url.hostname !== host
    || url.protocol !== 'https:'
    || url.username !== ''
    || url.password !== ''
    || url.port !== ''
    || url.search !== ''
    || url.hash !== ''
    || url.pathname !== WORKSPACE_EXPORT_PATH
    || raw !== url.toString()
  ) {
    throw malformed('the workspace download address is not this workspace\'s');
  }
  return raw;
}

async function errorFrom(response: Response): Promise<ApiClientError> {
  let code = 'request_failed';
  let message = `the API returned HTTP ${response.status}`;
  try {
    const payload = (await response.json()) as { error?: { code?: unknown; message?: unknown } };
    if (typeof payload?.error?.code === 'string') code = payload.error.code;
    if (typeof payload?.error?.message === 'string') message = payload.error.message;
  } catch {
    // keep the generic status text
  }
  return new ApiClientError('http', response.status, code, message);
}

async function postForZip(path: string, filename: string): Promise<ExportFile> {
  const controller = new AbortController();
  const timer = globalThis.setTimeout(() => controller.abort(), DOWNLOAD_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method: 'POST',
      credentials: 'include',
      signal: controller.signal,
      body: '{}',
      headers: { accept: 'application/zip', 'content-type': 'application/json' },
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiClientError('timeout', 0, 'request_timeout', 'the export took too long');
    }
    throw new ApiClientError('network', 0, 'network_error', 'the API could not be reached');
  } finally {
    globalThis.clearTimeout(timer);
  }
  if (!response.ok) throw await errorFrom(response);
  const type = response.headers.get('content-type') || '';
  if (!type.toLowerCase().startsWith('application/zip')) throw malformed('the export was not a zip archive');
  return { blob: await response.blob(), filename };
}

/** `POST /v1/projects/{id}/export`: one project's control-plane archive. */
export function downloadProjectExport(projectId: string, slug: string): Promise<ExportFile> {
  return postForZip(`/v1/projects/${encodeURIComponent(projectId)}/export`, safeFilename(slug, 'project'));
}

/** `POST /v1/account/export`: every project's control-plane archive plus the account records. */
export function downloadAccountExport(): Promise<ExportFile> {
  return postForZip('/v1/account/export', 'auteur-account-export.zip');
}

/** The part of `document` and `URL` the save step needs (a seam for tests). */
export interface SaveEnvironment {
  createObjectURL(blob: Blob): string;
  revokeObjectURL(url: string): void;
  createElement(tagName: 'a'): HTMLAnchorElement;
  body: { appendChild(node: HTMLAnchorElement): unknown; removeChild(node: HTMLAnchorElement): unknown };
}

/** Save a built archive through a temporary object URL; nothing is uploaded and the URL is revoked. */
export function saveExportFile(file: ExportFile, env?: SaveEnvironment): void {
  const target: SaveEnvironment = env ?? {
    createObjectURL: (blob) => URL.createObjectURL(blob),
    revokeObjectURL: (url) => URL.revokeObjectURL(url),
    createElement: (tag) => document.createElement(tag),
    body: document.body,
  };
  const url = target.createObjectURL(file.blob);
  const anchor = target.createElement('a');
  anchor.href = url;
  anchor.download = file.filename;
  anchor.style.display = 'none';
  target.body.appendChild(anchor);
  try {
    anchor.click();
  } finally {
    target.body.removeChild(anchor);
    target.revokeObjectURL(url);
  }
}

/** A customer-facing sentence for a refused export. The API's codes are canned; nothing is echoed. */
export function exportFailureMessage(failure: unknown): string {
  if (failure instanceof ApiClientError) {
    switch (failure.code) {
      case 'export_too_large': return 'This export is too large to build here. Contact support and we will run it for you.';
      case 'export_in_progress': return 'An export is already running. Try again in a minute.';
      case 'rate_limited': return 'Too many export requests. Try again later.';
      case 'forbidden': return 'You do not have permission to export this.';
      case 'not_found': return 'That project is not available to your account.';
      case 'request_timeout': return 'The export took too long. Try again, or contact support.';
      default: return 'The export could not be completed.';
    }
  }
  return 'The export could not be completed.';
}
